import React, { Fragment } from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import { makeStyles } from '@material-ui/core/styles';
import { PhaseTypeEnum } from '@vatos-pas/common';
import { useStyles as useBaseStyles } from '../../views/master-sheet-styles';
import { useWorkflowConfig } from '../../config';
import {
  CAN_OVERRIDE_CONTRACTOR,
  CAN_TOGGLE_GARAGE,
  CAN_APPROVE_PHASE,
  CAN_LOG_GARAGE,
} from 'providers/permissions';
import { SKIPPED_PHASE_DISPLAY } from 'utils/phaseUtils';
import { PhaseApprovedGeoIcon } from './PhaseApprovedGeoIcon';
import {
  getResourceRequested,
  getResourceAllocated,
  getSupervisorApproved,
  getRequestedBy,
  getStartDate,
  getApprovedDate,
  showCameraPhase,
  isApproved,
  showPhaseImages,
} from './useTimelineLogic';
import ContractorChangeDialog from './ContractorChangeDialog';
import ContractorHistoryDialog from './ContractorHistoryDialog';
import { RequestResourceModal } from './RequestResourceModal';
import { PhotoUploadDialog } from './PhotoUploadDialog';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { getPhaseDisplayName } from '../../constants/phaseConfigurations';
import { dateWithoutServerTimezone } from '../../utils/dateWithoutServerTimezone';

const BUMP_PHASES = ['Bump1', 'Bump2', 'Bump3', 'Bump4', 'Bump5'];

const styles = makeStyles({
  boxTitlePhases: {
    background: '#f2f2f2',
    borderRadius: '5px',
    boxShadow:
      '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
  },
  titlePhases: {
    fontWeight: 'bold',
  },
  accordion: {
    marginBottom: '10px',
    borderRadius: '5px !important',
    boxShadow:
      '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
    '&:before': {
      display: 'none',
    },
  },
});

interface MobileTimelineViewProps {
  logic: any;
}

export const MobileTimelineView: React.FC<MobileTimelineViewProps> = ({
  logic,
}) => {
  const classes = useBaseStyles();
  const innerStyles = styles();
  const config = useWorkflowConfig();

  const {
    selectedJob,
    refreshJobPhases,
    applyTimelinePayload,
    hasPermission,
    openContractorModal,
    setOpenContractorModal,
    selectedJobPhaseForEdit,
    openHistoryModal,
    setOpenHistoryModal,
    selectedJobPhaseForHistory,
    openRequestResource,
    setOpenRequestResource,
    selectedJobPhaseForModal,
    setSelectedJobPhaseForModal,
    selectedPhaseType,
    setSelectedPhaseType,
    openPhotoUpload,
    setOpenPhotoUpload,
    selectedJobPhaseForPhotos,
    isViewPhotosMode,
    toggleGarage,
    setToggleGarage,
    openConfirmGoBack,
    setOpenConfirmGoBack,
    openInitialDate,
    setOpenInitialDate,
    openInitialFinishingDate,
    setOpenInitialFinishingDate,
    initialDate,
    initialFinishingDate,
    changedBySupervisor,
    changedBySupervisorFinishing,
    getPhase,
    phasesToRender,
    onChangeGarage,
    handleGoBack,
    goBackJob,
    handleRequestResource,
    handleLogGarage,
    handleApprove,
    handleEditContractor,
    handleViewPhotos,
    handleHistoryModal,
    needStrike,
    getNextStatusText,
    checkRequestResourcePermission,
    isRequestResourceDisabled,
    getRequestButtonLabel,
    isGoBackDisabled,
    isGarageDisabled,
    approveJob,
    geoLocationError,
    geoCoords,
    geoAccuracy,
    geoLoading,
    requestLocation,
    getNextStatusTextProp,
    goBackWarningMessage,
  } = logic;

  const isBumpoutActive =
    config.timeline.bumpoutTimesheetDesktopBehaviors &&
    selectedJob?.job?.reqBumpoutTimesheet;

  if (!selectedJob) {
    return <div>Loading...</div>;
  }

  const checkPhaseStarted = (phaseType: PhaseTypeEnum) => {
    const phase = getPhase(selectedJob.jobPhases, phaseType);
    if (!phase) return false;
    return phase.priority <= (selectedJob.jobPhase?.priority || 0);
  };

  const getAccordionDetails = (phaseType: PhaseTypeEnum) => {
    const phaseApproved = isApproved(phaseType, selectedJob, getPhase);
    const isBump = BUMP_PHASES.includes(phaseType);
    const isBumpoutBump = isBumpoutActive && isBump;
    const resourceRequested = getResourceRequested(
      phaseType,
      selectedJob,
      getPhase,
    );
    const isBumpoutTimesheetFlow =
      (selectedJob as any)?.job?.reqBumpoutTimesheet || false;
    const canEditContractor = isBump
      ? resourceRequested && !phaseApproved
      : phaseApproved;
    const allocatedName = getResourceAllocated(
      phaseType,
      selectedJob,
      getPhase,
      isBump,
    );
    const isSkipped = allocatedName === SKIPPED_PHASE_DISPLAY;

    return (
      <Box>
        <Box mb={2}>
          <Typography>
            Requested by: <br />
            {getRequestedBy(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography>
            Resource requested: <br />
            {getResourceRequested(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        <Box mb={2} display="flex" alignItems="center">
          <>
            <Typography
              onClick={
                isSkipped || isBumpoutBump
                  ? undefined
                  : () => handleHistoryModal(phaseType)
              }
              className={
                isSkipped || isBumpoutBump
                  ? undefined
                  : needStrike(phaseType)
                  ? classes.striked
                  : classes.normal
              }
            >
              Resource Allocated: <br />
              {allocatedName}
            </Typography>
            {!isSkipped &&
              hasPermission(CAN_OVERRIDE_CONTRACTOR) &&
              (isBumpoutTimesheetFlow ? canEditContractor : phaseApproved) && (
                <EditIcon
                  onClick={() => handleEditContractor(phaseType)}
                  className={classes.editIcon}
                />
              )}
          </>
        </Box>
        <Box mb={2} display="flex">
          <Typography>
            Start date: <br />
            {getStartDate(phaseType, selectedJob, getPhase)}
          </Typography>
          {phaseType === PhaseTypeEnum.Spraying && initialDate && (
            <InfoIcon
              className={classes.info}
              onClick={() => setOpenInitialDate(true)}
            />
          )}
          {phaseType === PhaseTypeEnum.Finishing && initialFinishingDate && (
            <InfoIcon
              className={classes.info}
              onClick={() => setOpenInitialFinishingDate(true)}
            />
          )}
        </Box>
        <Box mb={2}>
          <Typography>Approved date:</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Typography>
              {getApprovedDate(phaseType, selectedJob, getPhase)}
            </Typography>
            <PhaseApprovedGeoIcon
              phaseType={phaseType}
              selectedJob={selectedJob}
            />
          </Box>
        </Box>
        <Box mb={2}>
          <Typography>
            Approved by <br />
            {getSupervisorApproved(phaseType, selectedJob, getPhase)}
          </Typography>
        </Box>
        <Box>
          {!isBumpoutBump &&
            !showCameraPhase(phaseType, selectedJob, getPhase) &&
            showPhaseImages(phaseType, selectedJob, getPhase) && (
              <CameraAltIcon
                className={classes.pointer}
                onClick={() => handleViewPhotos(phaseType)}
              />
            )}
        </Box>
      </Box>
    );
  };

  const phaseType = selectedJob.jobPhase?.phaseType as PhaseTypeEnum;
  const phaseApproved = isApproved(phaseType, selectedJob, getPhase);
  const isBump = BUMP_PHASES.includes(phaseType);
  const isBumpoutBump = isBumpoutActive && isBump;
  const currentResourceRequested = getResourceRequested(
    phaseType,
    selectedJob,
    getPhase,
  );
  const currentAllocatedName = getResourceAllocated(
    phaseType,
    selectedJob,
    getPhase,
  );
  const isCurrentSkipped = currentAllocatedName === SKIPPED_PHASE_DISPLAY;

  return (
    <Box px={1.5}>
      <Box mb={2} mt={2}>
        <Box>
          <Typography className={innerStyles.titlePhases}>
            Current Phase:
          </Typography>
        </Box>

        <Box width="100%" mt={1} className={innerStyles.boxTitlePhases}>
          <Box display="flex" alignItems="center" p={2}>
            {geoLocationError && (
              <Typography className={classes.geoLocationError}>
                {geoLocationError}
              </Typography>
            )}
            <Box width="100%">
              <Typography className={classes.titleFont}>
                {getNextStatusTextProp
                  ? getNextStatusTextProp(selectedJob)
                  : getNextStatusText(selectedJob)}
              </Typography>

              <Box display="flex" justifyContent="space-between">
                <Box mt={2}>
                  <Box mb={2} display="flex">
                    <Typography
                      onClick={
                        isCurrentSkipped || isBumpoutBump
                          ? undefined
                          : () => handleHistoryModal(phaseType)
                      }
                      className={
                        isCurrentSkipped || isBumpoutBump
                          ? undefined
                          : needStrike(phaseType)
                          ? classes.striked
                          : classes.normal
                      }
                    >
                      Resource Allocated: <br />
                      {currentAllocatedName}
                    </Typography>
                    {!isCurrentSkipped &&
                      hasPermission(CAN_OVERRIDE_CONTRACTOR) &&
                      (isBumpoutBump
                        ? currentResourceRequested && !phaseApproved
                        : !isBump && phaseApproved) && (
                        <EditIcon
                          onClick={() => handleEditContractor(phaseType)}
                          className={classes.editIcon}
                        />
                      )}
                  </Box>
                  <Box mb={2} display="flex">
                    <Typography>
                      Start date: <br />
                      {getStartDate(phaseType, selectedJob, getPhase)}
                    </Typography>
                    {phaseType === PhaseTypeEnum.Spraying && initialDate && (
                      <InfoIcon
                        className={classes.info}
                        onClick={() => setOpenInitialDate(true)}
                      />
                    )}
                    {phaseType === PhaseTypeEnum.Finishing &&
                      initialFinishingDate && (
                        <InfoIcon
                          className={classes.info}
                          onClick={() => setOpenInitialFinishingDate(true)}
                        />
                      )}
                  </Box>
                </Box>

                <Box mt={2}>
                  {!isBumpoutBump &&
                    showCameraPhase(phaseType, selectedJob, getPhase) &&
                    hasPermission(CAN_APPROVE_PHASE) && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.buttonTimeline}
                        onClick={() => handleApprove(phaseType)}
                      >
                        Approve
                      </Button>
                    )}
                </Box>
              </Box>

              {checkRequestResourcePermission() && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  mt={2}
                >
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.buttonTimeline}
                    onClick={handleGoBack}
                    disabled={isGoBackDisabled()}
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.buttonTimeline}
                    onClick={() => {
                      if (
                        hasPermission(CAN_LOG_GARAGE) &&
                        selectedJob?.jobPhase?.priority === 200
                      ) {
                        handleLogGarage();
                      } else {
                        handleRequestResource();
                      }
                    }}
                    disabled={
                      !selectedJob ||
                      !checkRequestResourcePermission() ||
                      isRequestResourceDisabled()
                    }
                  >
                    {getRequestButtonLabel()}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {phasesToRender.map((phaseTypeInList: PhaseTypeEnum) => {
        const phaseDisplayName = getPhaseDisplayName(phaseTypeInList);
        const isGaragePhase = phaseTypeInList === PhaseTypeEnum.Garage;

        if (isGaragePhase) {
          const garagePhase = getPhase(
            selectedJob.jobPhases,
            PhaseTypeEnum.Garage,
          );
          const hangingPhase = getPhase(
            selectedJob.jobPhases,
            PhaseTypeEnum.Hanging,
          );
          const disableGarageDetails =
            hangingPhase?.dateApproved && !garagePhase?.dateApproved;

          return (
            <Accordion
              key={phaseTypeInList}
              className={innerStyles.accordion}
              disabled={
                disableGarageDetails || !checkPhaseStarted(phaseTypeInList)
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{phaseDisplayName}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Box width="100%" mt={-2} mb={2}>
                    {hasPermission(CAN_TOGGLE_GARAGE) && (
                      <Switch
                        checked={toggleGarage}
                        onChange={() =>
                          onChangeGarage(
                            selectedJob,
                            toggleGarage,
                            refreshJobPhases,
                            setToggleGarage,
                            getPhase,
                          )
                        }
                        color="primary"
                        size="small"
                        name="checkedB"
                        disabled={isGarageDisabled()}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    )}
                  </Box>

                  {getAccordionDetails(phaseTypeInList)}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        }

        return (
          <Accordion
            key={phaseTypeInList}
            className={innerStyles.accordion}
            disabled={!checkPhaseStarted(phaseTypeInList)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{phaseDisplayName}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {getAccordionDetails(phaseTypeInList)}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {selectedJobPhaseForEdit && (
        <ContractorChangeDialog
          regionId={selectedJob?.job?.regionId}
          handleClose={() => setOpenContractorModal(false)}
          open={openContractorModal}
          onTimelinePayload={applyTimelinePayload}
          selectedJobPhase={selectedJobPhaseForEdit}
          job={selectedJob}
        />
      )}

      {selectedJobPhaseForHistory && (
        <ContractorHistoryDialog
          handleClose={() => setOpenHistoryModal(false)}
          open={openHistoryModal}
          selectedJobPhase={selectedJobPhaseForHistory}
        />
      )}

      {selectedJobPhaseForModal && selectedPhaseType && (
        <RequestResourceModal
          open={openRequestResource}
          handleClose={() => {
            setOpenRequestResource(false);
            setSelectedJobPhaseForModal(null);
            setSelectedPhaseType(null);
          }}
          phaseType={selectedPhaseType}
          selectedJobPhase={selectedJobPhaseForModal}
          job={selectedJob}
          regionId={selectedJob?.job?.regionId}
          refreshJobPhases={refreshJobPhases}
          onTimelinePayload={applyTimelinePayload}
          geoCoords={geoCoords}
          geoAccuracy={geoAccuracy}
          geoLoading={geoLoading}
          requestLocation={requestLocation}
          geoError={geoLocationError}
        />
      )}

      {selectedJobPhaseForPhotos && (
        <PhotoUploadDialog
          open={openPhotoUpload}
          handleClose={() => setOpenPhotoUpload(false)}
          selectedPhase={selectedJobPhaseForPhotos}
          isViewPhotos={isViewPhotosMode}
          job={selectedJob}
          approveJob={isViewPhotosMode ? undefined : approveJob}
          geoCoords={geoCoords}
          geoAccuracy={geoAccuracy}
          geoLoading={geoLoading}
          requestLocation={requestLocation}
          geoError={geoLocationError}
        />
      )}

      <ConfirmDialog
        open={openConfirmGoBack}
        title={goBackWarningMessage}
        handleClose={() => setOpenConfirmGoBack(false)}
        onConfirm={goBackJob}
      />

      <ConfirmDialog
        open={openInitialDate}
        title=""
        content={
          <>
            <p>
              Initial Start date:{' '}
              {initialDate
                ? dateWithoutServerTimezone(initialDate).toLocaleDateString()
                : ''}
            </p>
            {changedBySupervisor && <p>Note: Supervisor adjusted date</p>}
          </>
        }
        handleClose={() => setOpenInitialDate(false)}
      />
      <ConfirmDialog
        open={openInitialFinishingDate}
        title=""
        content={
          <>
            <p>
              Initial Start date:{' '}
              {initialFinishingDate
                ? dateWithoutServerTimezone(
                    initialFinishingDate,
                  ).toLocaleDateString()
                : ''}
            </p>
            {changedBySupervisorFinishing && (
              <p>Note: Supervisor adjusted date</p>
            )}
          </>
        }
        handleClose={() => setOpenInitialFinishingDate(false)}
      />
    </Box>
  );
};

export default MobileTimelineView;
